import { AfterViewInit, Component, Input, OnDestroy, OnInit, Output, ViewChild, EventEmitter } from '@angular/core';
import { Table, TableColumn } from 'src/app/base/interfaces/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { ContentService } from 'src/app/base/services/content/content.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PageActionResponse } from 'src/app/base/interfaces/pageActionResponse';
import { Button } from 'src/app/base/interfaces/button';
import { ButtonService } from 'src/app/base/services/buttons/buttons.service';
import { environment } from 'src/environments/environment';
import { AppidService } from 'src/app/base/services/appid/appid.service';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.css']
})
export class TableComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() table: Table;
    @Input() reloadNotifier: Observable<any>;
    @Input() reloadWithDataNotifier: Observable<any>;
    @Input() pageID: number;
    @Input() pageProvidedTableData? = null;

    @Output() pageAction: EventEmitter<PageActionResponse> = new EventEmitter<PageActionResponse>();
    @Output() needsLoadingAnimation: EventEmitter<boolean> = new EventEmitter<boolean>();

    protected reloadSubscription: Subscription;
    protected reloadWithDataSubscription: Subscription;

    @ViewChild(MatPaginator) paginator: MatPaginator;

    dataSource: TableDataSource;
    columnStrings: string[] = null;

    tableSortOrderText = false;
    tableOrder!: { columnName: string, columnOrder: string } | null;
    activeColumn;
    activeColumnIndex;
    sortDirection: SortDirection = '';
    
    displayDownloadIconsInApp: boolean;
    public appID: number;

    public visibleRowsCount = 0;

    //store any IDs selected by checkbox for future usage
    selectedIDs: any[] = [];
    
    //if reload params are set, use it.
    reloadParams: Record<string, any> = {};

    // List of supported file formats and their MIME types. Add new formats here without modifying exportFile function parameters.
    MimeTypes: Record<string, string> = {
        txt: "text/plain",
        xls: "application/octet-stream",
        pdf: "application/octet-stream"
    };

    public initialTableLoad: boolean = true;
    public isExpand:boolean = true;
    showSearch: boolean = false;
    constructor(public contentService: ContentService, public router: Router, public route: ActivatedRoute, public appIdService: AppidService, public buttonService?: ButtonService) {
        this.appIdService.appID$.pipe(take(1)).subscribe((appID) => {
            this.appID = appID;
            this.displayDownloadIconsInApp = this.appIdService.displayDownloadIconsInApp;
        });
    }
    

    ngOnInit(): void {

        // Setup the reloadParams to be the queryParams
        // This will contain any custom parameters when a table is reloaded
        this.reloadParams = { ...this.route.snapshot.queryParams };

        this.reloadSubscription = this.reloadNotifier.subscribe((x) => {

            // TO DO: reloadParams logic for fully V2 apps has not yet been tested. When needed test the following line
            this.reloadParams = x || {};
            this.dataSource.refetchDataOnLoad();
            this.loadTableData();
        });

        if (!this.displayDownloadIconsInApp) {
            this.table.DisplayDownloadIcons = 0;
        }

        // Check if an order has already been applied before
        const sortColumn = this.table.SortColumnName;
        const sortColumnOrder = this.table.SortColumnOrder;

        if (sortColumn && sortColumnOrder) {

            // Get column index saved in `sortColumn`
            const match = sortColumn.match(/\d+/);
            const columnOrderIndex = match ? parseInt(match[0], 10) : null;

            // Get column name saved in `sortColumn`
            const afterEqual = sortColumn.split("=")[1] || "";
            const sortColumnName = afterEqual
                .replace(/_/g, " ")
                .replace(/\b\w/g, char => char.toUpperCase());

            this.tableSortOrderText = true;
            this.activeColumn = sortColumnName;
            this.activeColumnIndex = columnOrderIndex;

            // If the value stored in `sortColumnOrder` is valid, we assign it to `sortDirection`. If not, we leave an empty string ('') to avoid errors, since `matSortDirection` (used in HTML) only accepts 'asc', 'desc' or ''.
            const sortColumnOrderLC = sortColumnOrder.toLowerCase();
            this.sortDirection = 
                (sortColumnOrderLC === 'asc' || sortColumnOrderLC.toLowerCase() === 'desc') 
                ? sortColumnOrderLC.toLowerCase() as SortDirection : '';

            this.tableOrder = {
                columnName: sortColumnName,
                columnOrder: this.sortDirection
            };
        }

        this.columnStrings = [];
        this.table.columns.forEach(x => this.columnStrings.push(x.header));
        this.dataSource = new TableDataSource(this.contentService, this.route, this.pageProvidedTableData);

        this.dataSource.loading$.subscribe(x => {
            if (x.loading) {
                this.needsLoadingAnimation.emit(true);
                this.initialTableLoad = true;
            } else {
                this.initialTableLoad = false;
                this.table.totalRecords = x.size;
                this.needsLoadingAnimation.emit(false);
            }
        })

		// When page with table is loaded.
        this.route.queryParams.subscribe(x => {
            //merge table initialParams with query params
            let params = Object.assign({}, this.table.InitialParams, x);

            this.table.InitialParams = null;
            this.loadTableData();
        });
    }

    generateButtons(buttons): boolean {
        return this.buttonService.generateButtons(buttons)
    }
    
    isExpandToggle(column: TableColumn){
        if (column.collapsible) this.isExpand=!this.isExpand;
    }

    ngAfterViewInit(): void {
        this.paginator.page.subscribe((x: PageEvent) => {
            this.loadTableData();
        });
    }

    ngOnDestroy(): void {
        this.reloadSubscription.unsubscribe();
    }

    /**
     * Exports a table in the specified format and triggers a file download.
     * The function dynamically validates allowed formats based on `MimeTypes`.
     *
     * @param format - The file format (must be a key from `MimeTypes`).
     * @param filename - The name of the downloaded file.
    */
    exportFile(format: keyof typeof this.MimeTypes, filename: string): void {
        this.needsLoadingAnimation.emit(true);
        this.contentService.exportTable(this.table.tableID, this.pageID, format).subscribe({
            next: (x) => {
                let binaryData = [x];

                let downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: this.MimeTypes[format] }));
                downloadLink.setAttribute('download', filename);
                document.body.appendChild(downloadLink);

                downloadLink.click();
                downloadLink.parentNode.removeChild(downloadLink);
                this.needsLoadingAnimation.emit(false);
            }, error: (error) => {
                console.error("Error exporting file:", error);
                this.needsLoadingAnimation.emit(false);
            }
        });
    }

    private loadTableData() {

        this.dataSource.loadData(
            this.table.tableID, 
            this.activeColumnIndex?.toString() || '', // If activeColumnIndex is defined it means that we have a column sorted.
            this.sortDirection || 'asc', // Default to 'asc' if sortDirection is not set, but it will be ignored in the backend if activeColumnIndex is not set.
            1, // Paginator in page 1.
            this.paginator?.pageSize || 25,
            this.pageID, 
            this.route.snapshot.queryParams // In case we have filter parameters in the URL, we send them to the backend to filter the data accordingly on initial load.
        );
    }

    // Function is called when user clicks the column header to sort the data and save the sort in DB
    sortData(event) {

        // We don't want to show the text when the user reorders the table, it's not necessary
        this.tableSortOrderText = false;

        this.dataSource.loadData(
            this.table.tableID, 
            event.active, 
            event.direction, 
            this.paginator.pageIndex + 1, 
            this.paginator.pageSize, 
            this.pageID
        );
        
        // In order to display the sort orderText and the arrow, I need to get both values
        const columnIndex = parseInt(event.active, 10);
        const columnName = this.table.columns[columnIndex]?.header;
        let selectedColumn;

        // We do this conversion to save the value in the DB, this way we keep the same type of value stored
        const snakeCaseName = columnName
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^\w]/g, '');
    
        selectedColumn = `Column${columnIndex}=${snakeCaseName}`;

        this.contentService.upsertTablePersonViews(
            this.table.tableID, 
            selectedColumn, 
            event.direction, 
            this.paginator.pageSize, 
            this.pageID,
        ).subscribe({
            error: console.error
        });
    }

    toggleSearch() {
        if (this.showSearch) {
            //clear filter
            this.loadTableData();
        }
        this.showSearch = !this.showSearch;
    }

    filter($event, colIndex) {
        //get value of field based on event
        let value = $event.target.value;
        this.dataSource.filterData(colIndex, value);
    }

    /**
     * Parses a special string in the format [[link]]:{...} that is generated by BuildLink in PageBase.php and returns an object
     * with linkText, pageID, and additionalParameters for navigation links.
	 * This is only to build links in tables with no callback. For links with callback, use buildCallbackLink instead.
    */
    buildLink(rowText: string): any {
        //convert [[link]]:{"linkText":"Details","pageID":999,"additionalParameters":[]} to object
        let link = JSON.parse(rowText.replace("[[link]]:", ""));
        return {
            linkText: link.linkText,
            pageID: link.pageID,
            additionalParameters: link.additionalParameters
        };
    }

    /**
     * Extracts and return the URL from additionalParameters.
    */
    getUrlFromLink(rowText: string): string | null {

        let envURL = environment.urls.core;
        const link = this.buildLink(rowText);
        return envURL + link.additionalParameters.URL;
    }

    /**
     * Checks if the cell contains a redirect link [[link]]:{...}
     */
    hasLink(rowText: string): boolean {
        if (rowText == null) return false;
        
        rowText = rowText.toString();
        return rowText.indexOf("[[link]]") > -1 || rowText.indexOf("[[edit]]") > -1 || rowText.indexOf("[[delete]]") > -1 || rowText.indexOf("[[callbackLink]]") > -1;
    }

    redirectLink(rowText: string): boolean {
        if (rowText == null) return false;

        rowText = rowText.toString();
        return rowText.indexOf("[[link]]") > -1;
    }

    /**
     * Handles navigation when a link cell is clicked.
     * Builds the query parameters from the link object and navigates using Angular Router.
     */
    tableLinkClick(link: any) {

        // Setting msg and page_id to null removes it from the URL on merge, 
        // preventing the previous submit message and page id from showing on the next page load.
        // The page parameter (first one) is the real page ID.
        let params = { page: link.pageID, msg: null, page_id: null }; 

        //iterate through action.options["additionalParams"] record and add to params.
        for (let paramKey in link.additionalParameters) {
            params[paramKey] = link.additionalParameters[paramKey];
        }

        this.router.navigate(
            [],
            {
            relativeTo: this.route,
            queryParams: params,
            queryParamsHandling: 'merge'
        });
    }

    /**
     * Parses a special string in the format [[callbackLink]]:{...} and returns an object
     * with linkText, callback, and additionalParameters for callback actions.
     */
    buildCallbackLink(rowText: string): any {
        let link = JSON.parse(rowText.replace("[[callbackLink]]:", ""));
        return {
            linkText: link.linkText,
            callback: link.callback,
            additionalParameters: link.additionalParameters
        };
    }

    /**
     * Checks if the cell contains a callback link ([[callbackLink]]).
     * Returns true if found, otherwise false.
     */
    hasCallbackLink(rowText: string): boolean {
        if (rowText == null) return false;

        rowText = rowText.toString();
        return rowText.includes("[[callbackLink]]");
    }

    /**
     * Handles callback actions when a callback link cell is clicked.
     * Sends the callback and parameters to the backend via ContentService.
     */
    handleCallbackLink(link: any): void {
        link.additionalParameters["tableCheckboxes"] = this.selectedIDs;

        this.contentService.submitTableLink(this.table.tableID, link.callback, link.additionalParameters).subscribe({
            next: (x) => {
                this.pageAction.emit(x);
            },
            error: (err) => console.error(err)
        });
    }

    /**
     * Parses a special string in the format view||?nav_id=...&page_id=... and returns the link.
     */
    buildViewLink(rowText: string): any {
		
        // This is a temporary solution for a 'view' link, this is used when tables comes from V1 because this action is built in this way
        let link = rowText.replace("view||", "");
        return link
    }

    /**
     * Handles navigation for legacy view links by building the query params and navigating.
     */
    handleViewLink(link: any): void {
        this.router.navigateByUrl("/" + link);
    }

    checkboxChange(row) {
        let rowText: string = row.toString();
        let id = rowText.split(":")[1];

        //remove item from selectedIDs if it is already in there, add it if not
        if (this.selectedIDs.includes(id)) {
            this.selectedIDs.splice(this.selectedIDs.indexOf(id), 1);
        } else {
            this.selectedIDs.push(id);
        }
    }

    hasCheckbox(rowText: string): boolean {
        if (rowText == null) return false;

        rowText = rowText.toString();
        return rowText.includes("[[checkbox]]");
    }

    handleButtonClick(button: Button): void {

		// Without callback.
        if (button.Type == "link") {

            // Check if link is just applying a "page" parameter.
            if (button.Link.indexOf("page=") > -1) {

                const NavSourcev1 = localStorage.getItem('NavSourcev1');

                // If we are originally from V1, then send through the following variables as well so that the auth logic works.
                if (NavSourcev1) {
                    const params = new URLSearchParams({
                        NavSourceV1: NavSourcev1,
                        sid: localStorage.getItem('V1SID'),
                        org_id: localStorage.getItem('V1OrgID'),
                        V1UserID: localStorage.getItem('V1UserID')
                    });

                    button.Link += '&' + params.toString();
                }
                this.router.navigateByUrl(window.location.pathname + button.Link);
            }
        } else if (button.Type == "callback") {
            if (button.Options == null) button.Options = {};
            button.Options["tableCheckboxes"] = this.selectedIDs;

            //merge reloadparams into options
            if (this.reloadParams != null) {
                for (let paramKey in this.reloadParams) {
                    button.Options[paramKey] = this.reloadParams[paramKey];
                }
            }

			// This is for a button despite the name of the function that is called below.
            this.contentService.submitTableLink(this.table.tableID, button.Label, button.Options).subscribe({
                next: (x) => {
                    this.pageAction.emit(x);
                },
                error: (err) => console.error(err)
            });
        }
    }
    
}

export class TableDataSource extends DataSource<string> {
    private dataSubject = new BehaviorSubject<string[]>([]);
    private loadingSubject = new BehaviorSubject<any>({});

    public loading$ = this.loadingSubject.asObservable();

    private data = [];
    public dataLength = 0;

    private getDataAgain = true;
    
    // ActivatedRoute provides access to the current route's parameters, query parameters, and route data (e.g. navID).
    constructor(private contentService: ContentService, public route: ActivatedRoute, private pageProvidedTableData = null) {
        super();
    }

    connect(): Observable<string[]> {
        return this.dataSubject.asObservable();
    }

    disconnect(collectionViewer: CollectionViewer) {
        this.dataSubject.complete();
        this.loadingSubject.complete();
    }

    refetchDataOnLoad() {
        this.getDataAgain = true;
    }

    loadData(tableID: number, filter = '', sortOrder = 'asc', page = 1, pageSize = 25, pageID: number, additionalParameters?: Record<string, any>): void {
        this.loadingSubject.next({
            loading: true,
            size: 0
        });

        /**
         * We're switching to client-side pagination and sorting because the server
         * really doesn't need to handle it except for tables that are absolutely massive.
         * We fetch the data once on page-load and then just handle sorting/pagination in client from then-on.
         */

        if (this.data.length == 0 || this.getDataAgain) {
            //update 1/12/23: if we have pageProvidedTableData, we don't need to fetch it from the server (table file in backend)
            if (this.pageProvidedTableData != null) {
                this.getDataAgain = false;
                this.handleDataLoad(this.pageProvidedTableData);
                this.sortAndPaginate(filter, sortOrder, page, pageSize);
                return;
            }
            else {
                // If pageProvidedTableData is not available, fetch data from the server.
                this.contentService.getTableData(tableID, filter, sortOrder, page, pageSize, pageID, this.route.snapshot.data.nav, additionalParameters).subscribe({
                    next: (v) => {
                        this.getDataAgain = false;
                        this.handleDataLoad(v);
                        this.sortAndPaginate(filter, sortOrder, page, pageSize);
                    },
                    error: (e) => console.error(e)
                });
            }
        } else {
            this.sortAndPaginate(filter, sortOrder, page, pageSize);
        }
    }

    handleDataLoad(v) {
        this.data = v;
        this.dataLength = v.length;
    }

    setUpdatedPageProvidedData(v) {
        this.pageProvidedTableData = v;
        this.dataLength  = this.data.length;
    }

    sortAndPaginate(sortColumn = '', sortOrder = 'asc', page = 1, pageSize = 25) {
        //sort data
        let sortedData = [];
        if (sortColumn != '') {
            sortedData = this.data.sort((a, b) => {
                if (a[sortColumn] < b[sortColumn]) {
                    return sortOrder == 'asc' ? -1 : 1;
                } else if (a[sortColumn] > b[sortColumn]) {
                    return sortOrder == 'asc' ? 1 : -1;
                } else {
                    return 0;
                }
            });
        } else {
            sortedData = this.data;
        }

        let startIndex = (page - 1) * pageSize;
        let endIndex = startIndex + pageSize;
        let currentPage = sortedData.slice(startIndex, endIndex);

        this.dataSubject.next(currentPage);

        this.loadingSubject.next({
            loading: false,
            size: this.data.length
        });
    }

    filterData(columnIndex: number, filter: string) {
        let filteredData = this.data.filter(row => {
            if (row[columnIndex] != null) {
                let rowText: string = row[columnIndex].toString();
                return rowText.toLowerCase().includes(filter.toLowerCase());
            }
            return false;
        });

        this.dataSubject.next(filteredData);
    }
}